import {batch} from 'react-redux';

import {showLoading, updateLoading} from '@/actions/loadingActions';
import {hidePopUp, showPopUp} from '@/actions/modalActions';
import {showAlert} from '@/actions/setupActions';
import {setInstallSystemUpdate, setUpdateSWStatus, setSystemUpdatePending} from '@/actions/updateSystemAction';
import {getStatusUpdateSW, postCancelUpdateSW, postInstallUpdateSW, postManualUpdateSW} from '@/api/updateApi';
import {UpdateStateEnum, UpdateSWStatusType} from '@/constants/dataEnum';
import * as APPDATA from '@/constants/pcConstAppData';
import {formatMessage} from '@/libs/intlHelper';
import {clearItem, getItem, setItem} from '@/libs/systemUpdateStorage';

import {handleError} from './apiBusiness';

export function getSystemStatusUpdated() {
  return async (dispatch) => {
    try {
      if (!getItem()) return;

      const response = await getStatusUpdateSW();
      const {data} = response;

      if (!data) throw response;
      const {Status} = data;

      switch (Status) {
        case UpdateStateEnum.COMPLETE:
          dispatch(
            showAlert({
              alertShow: true,
              alertText: formatMessage({id: 'toast.systemUpdate.success.message'}),
              alertStyle: APPDATA.ENUM.ALERT_TYPE.ALERT_SUCCESS,
            }),
          );
          break;

        case UpdateStateEnum.FAILED:
          dispatch(
            showAlert({
              alertShow: true,
              alertText: formatMessage({id: 'toast.systemUpdate.failed.message'}),
              alertStyle: APPDATA.ENUM.ALERT_TYPE.ALERT_DANGER,
            }),
          );
          break;

        default:
          break;
      }

      clearItem();
    } catch (error) {
      console.log('getSystemStatusUpdated error', error);
    }
  };
}

export function changeSystemUpdateProgress(value) {
  return async (dispatch, getState) => {
    const {loadingStore} = {...getState()};
    const {showLoading, loadingText} = loadingStore;
    const message = formatMessage({id: 'loading.systemUpdate.updating.message'}, {value});

    if (showLoading && loadingText === message) return;

    dispatch(
      updateLoading({
        showLoading: true,
        loadingText: formatMessage({id: 'loading.systemUpdate.updating.message'}, {value}),
        loadingClickEvent: undefined,
      }),
    );
  };
}

function handleSystemUpdateValidation(updateProps = {}, isUpdateNow = true) {
  return async (dispatch) => {
    try {
      batch(() => {
        if (isUpdateNow) {
          dispatch(changeSystemUpdateProgress('Copying files...'));
        }

        // online mode
        dispatch(setSystemUpdatePending());
      });

      const _data = {...updateProps, IsUpdateNow: isUpdateNow};

      const response = await postInstallUpdateSW(_data);
      const {data} = response;

      if (!data) throw response;

      batch(() => {
        dispatch(setInstallSystemUpdate(response.data));

        if (!isUpdateNow) {
          dispatch(
            showAlert({
              alertShow: true,
              alertText: formatMessage({id: 'toast.systemUpdate.updateAfterRestart.message'}),
              alertStyle: APPDATA.ENUM.ALERT_TYPE.ALERT_INFO,
            }),
          );
        } else {
          dispatch(setUpdateSWStatus(UpdateSWStatusType.IN_PROGRESS));
        }

        setItem(true);
      });
    } catch (error) {
      console.log('handleLaterSystemUpdate error', error);
      dispatch(
        showAlert({
          alertShow: true,
          alertText: formatMessage({id: 'toast.systemUpdate.failed.message'}),
          alertStyle: APPDATA.ENUM.ALERT_TYPE.ALERT_DANGER,
        }),
      );
    } finally {
      dispatch(hidePopUp());
    }
  };
}

export function showSystemUpdateValidationModal(updateProps = {}) {
  return (dispatch) => {
    const popUpButton = [
      {
        id: APPDATA.RESTART,
        isPrimaryButton: true,
        onClick: () => dispatch(handleSystemUpdateValidation(updateProps, true)),
        label: formatMessage({id: 'modal.systemUpdateValidation.primaryButton'}),
      },
      {
        id: APPDATA.LATER,
        isPrimaryButton: false,
        onClick: () => dispatch(handleSystemUpdateValidation(updateProps, false)),
        label: formatMessage({id: 'modal.systemUpdateValidation.secondaryButton'}),
      },
    ];
    dispatch(
      showPopUp({
        popUpTitle: formatMessage({id: 'modal.systemUpdateValidation.title'}),
        popUpText: formatMessage({id: 'modal.systemUpdateValidation.message'}),
        popUpButton,
        popUpClose: () => dispatch(handleCloseSystemUpdate()),
        popUpHideCloseButton: true,
      }),
    );
  };
}

// #region OFFLINE

export function showDowngradeOfflineModal(updateProps) {
  return (dispatch) => {
    const popUpButton = [
      {
        id: APPDATA.MESSAGES.SOFTWARE_UPDATE.ALLERT.UPDATE_OFFLINE[APPDATA.ENUM.SOFTWARE_UPDATE_ERROR.OUTDATED].BUTTON,
        isPrimaryButton: true,
        onClick: () => dispatch(showSystemUpdateValidationModal(updateProps)),
        label: formatMessage({id: 'modal.downgradeSystemUpdateOffline.primaryButton'}),
      },
      {
        id: APPDATA.CANCEL,
        isPrimaryButton: false,
        onClick: () => dispatch(handleCloseSystemUpdate()),
        label: formatMessage({id: 'modal.downgradeSystemUpdateOffline.secondaryButton'}),
      },
    ];
    dispatch(
      showPopUp({
        popUpTitle: formatMessage({id: 'modal.downgradeSystemUpdateOffline.title'}),
        popUpText: formatMessage({id: 'modal.downgradeSystemUpdateOffline.message'}),
        popUpButton,
        popUpClose: () => dispatch(handleCloseSystemUpdate()),
        popUpHideCloseButton: true,
      }),
    );
  };
}

/**
 *
 * @param {Function} action
 * @fixme please change implementation action param in this function, currently we still need to keep this action due to the state still in the local component
 */
export function showReinstallOfflineModal(updateProps) {
  return (dispatch) => {
    const popUpButton = [
      {
        id: APPDATA.MESSAGES.SOFTWARE_UPDATE.ALLERT.UPDATE_OFFLINE[APPDATA.ENUM.SOFTWARE_UPDATE_ERROR.SAME_VERSION]
          .BUTTON,
        isPrimaryButton: true,
        onClick: () => dispatch(showSystemUpdateValidationModal(updateProps)),
        label: formatMessage({id: 'modal.reinstallSystemUpdateOffline.primaryButton'}),
      },
      {
        id: APPDATA.CANCEL,
        isPrimaryButton: false,
        onClick: () => dispatch(handleCloseSystemUpdate()),
        label: formatMessage({id: 'modal.reinstallSystemUpdateOffline.secondaryButton'}),
      },
    ];
    dispatch(
      showPopUp({
        popUpTitle: formatMessage({id: 'modal.reinstallSystemUpdateOffline.title'}),
        popUpText: formatMessage({id: 'modal.reinstallSystemUpdateOffline.message'}),
        popUpButton,
        popUpClose: () => dispatch(handleCloseSystemUpdate()),
        popUpHideCloseButton: true,
      }),
    );
  };
}

export function openFileUpdaterOffline() {
  return async (dispatch, getState) => {
    try {
      const {setupStore} = {...getState()};
      const {selectedFileUpdate} = setupStore;
      const {Preview} = selectedFileUpdate;

      dispatch(
        updateLoading({
          showLoading: true,
          loadingText: formatMessage({id: 'loading.systemUpdate.checkingFile.message'}),
          loadingClickEvent: undefined,
        }),
      );

      const response = await postManualUpdateSW(Preview);

      if (!response.data) throw response;

      const {type, updateProps} = response.data;

      switch (type) {
        case APPDATA.ENUM.SOFTWARE_UPDATE_ERROR.OUTDATED:
          dispatch(showDowngradeOfflineModal(updateProps));
          break;
        case APPDATA.ENUM.SOFTWARE_UPDATE_ERROR.SAME_VERSION:
          dispatch(showReinstallOfflineModal(updateProps));
          break;
        default:
          dispatch(showSystemUpdateValidationModal(updateProps));
          break;
      }
    } catch (error) {
      console.log('openFileUpdaterOffline error', error);

      switch (error?.response?.data?.type) {
        // case APPDATA.ENUM.SOFTWARE_UPDATE_ERROR.OUTDATED:
        // case APPDATA.ENUM.SOFTWARE_UPDATE_ERROR.SAME_VERSION:
        //     dispatch(
        //         showAlert({
        //             alertShow: true,
        //             alertText: APPDATA.MESSAGES.SOFTWARE_UPDATE.ALLERT.UPDATE_OFFLINE[APPDATA.ENUM.SOFTWARE_UPDATE_ERROR.OUTDATED],
        //             alertStyle: APPDATA.ENUM.ALERT_TYPE.ALERT_DANGER,
        //         })
        //     );
        //     break;
        case APPDATA.ENUM.SOFTWARE_UPDATE_ERROR.CORRUPTED:
          dispatch(
            showAlert({
              alertShow: true,
              alertText: formatMessage({id: 'toast.systemUpdateOffline.corrupted.message'}),
              alertStyle: APPDATA.ENUM.ALERT_TYPE.ALERT_DANGER,
            }),
          );
          break;
        case APPDATA.ENUM.SOFTWARE_UPDATE_ERROR.LICENSE_EXPIRED:
          dispatch(
            showAlert({
              alertShow: true,
              alertText: formatMessage({id: 'toast.systemUpdateOffline.licenseExp.message'}),
              alertStyle: APPDATA.ENUM.ALERT_TYPE.ALERT_DANGER,
            }),
          );
          break;
        case APPDATA.ENUM.SOFTWARE_UPDATE_ERROR.INVALID_TIME:
          dispatch(
              showAlert({
                alertShow: true,
                alertText: formatMessage({id: 'toast.systemUpdateOffline.invalidTime.message'}),
                alertStyle: APPDATA.ENUM.ALERT_TYPE.ALERT_DANGER,
              }),
          );
          break;
        default:
          dispatch(handleError(error));
          break;
      }
    } finally {
      dispatch(showLoading(false));
    }
  };
}

export function handleCloseSystemUpdate() {
  return async (dispatch) => {
    try {
      await postCancelUpdateSW();

      dispatch(hidePopUp());
    } catch (error) {
      console.log('handleCloseSystemUpdateOffline error', error);
    }
  };
}
// #endregion
